import { parse } from 'date-fns';
import { calculateEntryAmount } from '@/services/utils';
angular.module('leanlaw').factory('billableDetailDefinition', ['$q', '$http', 'leanLawAuth', 'chargeManager', 'leanLawApi', 'expenseManager', function ($q, $http, leanLawAuth, chargeManager, leanLawApi, expenseManager) {
  const cancellers = {};
  const cancelRequest = path => {
    if (cancellers[path]) cancellers[path].resolve();
    delete cancellers[path];
  };
  const listBillables = (path, query) => {
    cancelRequest(path);
    cancellers[path] = $q.defer();
    const params = {
      ...query,
      timeOffset: new Date().getTimezoneOffset()
    };
    return $http.get(`${leanLawAuth.domain}/v1/${path}`, {
      params,
      timeout: cancellers[path].promise
    }).then(response => response.data);
  };

  // entries/wip-detail returns the same entries as GET /entries, without its paging totals and
  // scope expansion, which made this report take 30-50s for the largest firms.
  const ENTRIES_PATH = 'entries/wip-detail';
  const EXPENSES_PATH = 'expenses';
  const CHARGES_PATH = 'charges';
  const cancel = () => {
    [ENTRIES_PATH, EXPENSES_PATH, CHARGES_PATH].forEach(cancelRequest);
  };
  const basicInfo = cm => ({
    id: cm.id,
    label: cm.label,
    reference: cm.reference,
    name: cm.name
  });
  const clientMatterUserFromItem = e => ({
    user: e.account,
    matter: e.matter && basicInfo(e.matter),
    client: e.client ? basicInfo(e.client) : e.matter && basicInfo(e.matter.client)
  });
  let loaded = {
    queryJson: null,
    entries: null,
    expenses: null,
    charges: null
  };
  const loadItems = (query, filter, me) => {
    // clear cache if query changes
    const queryJson = JSON.stringify(query);
    if (loaded.queryJson !== queryJson) {
      loaded = {
        queryJson
      };
    }
    const loading = [];
    if (filter.showEntries) {
      loaded.entries = loaded.entries || listBillables(ENTRIES_PATH, query).then(list => {
        if (!list) return [];
        return list.entries.map(e => {
          // date-fns parse() treats YYYY-MM-DD as local time, avoiding the
          // UTC midnight shift that new Date("YYYY-MM-DD") causes in US timezones.
          const date = me.features?.dateHandling && e.localDateTime?.date ? parse(e.localDateTime.date, 'yyyy-MM-dd', new Date()) : new Date(e.startTime);
          return createEntryObject({
            ...e,
            date
          });
        });
      });
      loading.push(loaded.entries);
    }
    if (filter.showExpenses) {
      loaded.expenses = loaded.expenses || listBillables(EXPENSES_PATH, query).then(list => {
        if (!list) return [];
        return list.expenses.map(e => createExpenseObject(e));
      });
      loading.push(loaded.expenses);
    }
    if (filter.showCharges) {
      loaded.charges = loaded.charges || listBillables(CHARGES_PATH, query).then(list => {
        if (!list) return [];
        return list.charges.map(e => createChargeObject(e));
      });
      loading.push(loaded.charges);
    }
    return $q.all(loading).then(results => results.flat());
  };
  function createItemValues(item) {
    return {
      date: {
        value: item.date
      },
      client: {
        value: item.client && item.client.label
      },
      matter: {
        value: item.matter && item.matter.label
      },
      description: {
        value: item.description
      },
      type: {
        value: item.type
      },
      hours: {
        value: item.hours
      },
      rate: {
        value: item.rate
      },
      amount: {
        value: item.amount
      },
      billed: {
        value: item.billed
      }
    };
  }
  function createExpenseObject(e) {
    return {
      ...clientMatterUserFromItem(e),
      date: new Date(e.date),
      description: e.description,
      amount: e.amount,
      billed: e.billed,
      id: e.id,
      type: 'Expense'
    };
  }
  function createEntryObject(e) {
    return {
      ...clientMatterUserFromItem(e),
      date: e.date || new Date(e.startTime),
      description: e.description,
      hours: e.hours,
      rate: e.billingType === 'billable' && e.rate ? e.rate.amount : undefined,
      amount: e.billingType === 'billable' && e.rate ? calculateEntryAmount(e.rate.amount, e.hours) : 0,
      billed: e.billed,
      id: e.id,
      type: 'Time'
    };
  }
  function createChargeObject(charge) {
    return {
      ...clientMatterUserFromItem(charge),
      date: charge.date && new Date(charge.date),
      description: charge.description,
      amount: charge.amount,
      billed: charge.billed,
      id: charge.id,
      type: 'Fixed Fee'
    };
  }
  const convertItemsToRows = (items, onlyMyWipDetail) => {
    // group by user
    const users = items.reduce((list, item) => {
      let id = item.user && item.user.id;
      if (id === undefined) id = 'unassigned';
      if (!list[id]) list[id] = {
        user: item.user,
        items: []
      };
      list[id].items.push(item);
      return {
        ...list,
        unassigned: list.unassigned
      };
    }, {});
    const allUsers = {
      ...users,
      unassigned: users.unassigned
    };
    if (onlyMyWipDetail) {
      return {
        rows: items.map(item => ({
          item,
          values: createItemValues(item)
        }))
      };
    }
    const mappedUsers = Object.entries(allUsers);
    const unassignedIndex = mappedUsers.findIndex(([key, value]) => key === 'unassigned');
    if (unassignedIndex !== -1) {
      const [unassignedKey, unassignedValue] = mappedUsers.splice(unassignedIndex, 1)[0];
      mappedUsers.push([unassignedKey, unassignedValue]);
    }
    const assignedOrUnassignedUsers = Object.fromEntries(mappedUsers);

    // create rows and subrows for group of users and items
    const rows = Object.keys(assignedOrUnassignedUsers).map(id => {
      const user = assignedOrUnassignedUsers[id];
      let label = 'Not Assigned';
      if (user?.user) {
        const {
          name,
          initials
        } = user.user;
        label = initials ? `${name} (${initials})` : name;
      }
      return {
        id: id,
        label,
        italic: true,
        values: {
          date: {
            value: undefined
          },
          name: {
            value: undefined
          },
          initials: {
            value: undefined
          },
          client: {
            value: undefined
          },
          matter: {
            value: undefined
          },
          description: {
            value: undefined
          },
          type: {
            value: undefined
          },
          hours: {
            value: user?.items?.reduce((sum, i) => sum + (i.hours || 0), 0)
          },
          rate: {
            value: undefined
          },
          amount: {
            value: user?.items?.reduce((sum, i) => sum + i.amount, 0)
          },
          billed: {
            value: undefined
          }
        },
        rows: user?.items ? user?.items?.map(item => ({
          values: {
            date: {
              value: item.date
            },
            name: {
              value: user.user && user.user.name
            },
            initials: {
              value: user.user && user.user.initials
            },
            client: {
              value: item.client && item.client.label
            },
            matter: {
              value: item.matter && item.matter.label
            },
            description: {
              value: item.description
            },
            type: {
              value: item.type
            },
            hours: {
              value: item.hours
            },
            rate: {
              value: item.rate
            },
            amount: {
              value: item.amount
            },
            billed: {
              value: item.billed
            }
          }
        })) : []
      };
    });
    return {
      rows
    };
  };
  const load = ({
    query,
    filter,
    params,
    me
  }) => {
    const {
      onlyMyWipDetail
    } = params;
    query.billed = filter.includeBilled ? 'any' : false;
    query.billedState = undefined;
    query.excludeNoDate = true;
    query.account = onlyMyWipDetail ? me.id : null;
    return loadItems(query, filter, me).then(data => convertItemsToRows(data, onlyMyWipDetail, me));
  };
  const structure = ({
    params,
    me
  }) => {
    const {
      onlyMyWipDetail
    } = params;
    const columns = [{
      id: 'date',
      label: 'Date',
      filter: 'dateStandard'
    }, {
      id: 'name',
      label: 'User',
      show: (_, exporting) => exporting
    }, {
      id: 'initials',
      label: 'Initials',
      show: (_, exporting) => exporting
    }, {
      id: 'client',
      label: 'Client'
    }, {
      id: 'matter',
      label: me.terms.Matter
    }, {
      id: 'description',
      label: 'Description'
    }, {
      id: 'type',
      label: 'Type'
    }, {
      id: 'hours',
      label: 'Hours',
      filter: 'hoursDecimal',
      number: true
    }, {
      id: 'rate',
      label: 'Rate',
      filter: 'amount',
      number: true,
      total: false
    }, {
      id: 'amount',
      label: 'Amount',
      filter: 'amount',
      number: true
    }, {
      id: 'billed',
      label: 'Billed',
      filter: 'yesno',
      show: filter => filter.includeBilled
    }];
    if (onlyMyWipDetail) {
      // remove user and initials for my wip
      columns.splice(1, 2);
    }
    return {
      sort: 'date',
      label: `WIP Detail`,
      subrow: {
        label: onlyMyWipDetail ? null : 'Detail',
        default: !onlyMyWipDetail,
        title: !onlyMyWipDetail
      },
      totals: {
        rows: true,
        totalLabelCells: 1
      },
      showFilters: {
        matterForUser: !onlyMyWipDetail,
        distribution: false,
        itemType: true,
        includeBilled: true,
        dateRange: !!onlyMyWipDetail
      },
      export: {
        flatten: !onlyMyWipDetail
      },
      columns
    };
  };
  async function selectDone({
    fromState,
    fromParams
  }) {
    let item;
    let id;
    let itemDeleted = false;
    switch (fromState.name) {
      case 'reports.generic.charges':
        id = fromParams.chargeId;
        try {
          const charge = await chargeManager.get(fromParams.chargeId);
          item = createChargeObject(charge);
        } catch (e) {
          if (e.status === 404) {
            itemDeleted = true;
          }
        }
        break;
      case 'reports.generic.entry':
      case 'reports.generic.entry2':
        id = fromParams.chargeId || fromParams.entryId;
        try {
          const entry = await leanLawApi.getEntry({
            id: fromParams.chargeId || fromParams.entryId
          });
          item = createEntryObject(entry);
        } catch (e) {
          if (e.status === 404) {
            itemDeleted = true;
          }
        }
        break;
      case 'reports.generic.expenses':
        id = fromParams.expenseId;
        try {
          const expense = await expenseManager.get(fromParams.expenseId);
          item = createExpenseObject(expense);
        } catch (e) {
          if (e.status === 404) {
            itemDeleted = true;
          }
        }
        break;
      default:
        return;
    }
    return {
      values: itemDeleted ? {} : createItemValues(item),
      id,
      item,
      itemDeleted
    };
  }
  return {
    structure,
    load,
    cancel,
    selectDone
  };
}]);